import { ChevronDown, ChevronUp, Merge } from "lucide-react";
import { memo, useEffect, useRef } from "react";
import { VerdictToggle } from "@/components/VerdictToggle";
import { cn } from "@/lib/cn";
import { formatClockAt, formatDuration, formatTimecode } from "@/lib/format";
import { type DraftPart } from "@/lib/meetingParts";

/** One part of the draft as its divider and the part map draw it. */
export interface PartView {
  position: number;
  count: number;
  startMs: number;
  endMs: number;
  verdict: "meeting" | "discard";
  title: string;
  description: string;
  reason: string;
  people: { name: string; colour: string; share: number }[];
  /** No line of the transcript starts inside it — the recorder in a pocket. */
  empty: boolean;
  /** The line a line earlier / later this part could start at, or null where it cannot move. */
  earlier: { sequence: number; atMs: number } | null;
  later: { sequence: number; atMs: number } | null;
}

/** The part a moment falls in: the last one that has started by then. */
export function partOf(views: PartView[], atMs: number | null): PartView | undefined {
  let found: PartView | undefined;
  for (const view of views) {
    if (view.startMs <= (atMs ?? 0)) found = view;
    else break;
  }
  return found;
}

/**
 * The line a part starts under: a thick bar in the primary colour, then what the part is — its
 * number, span, title, description, who talks, why it starts here — and the controls that change
 * where it starts.
 *
 * Loud on purpose. A day-long recording reads as one wall of turns, and the one thing this mode
 * adds is where that wall divides; a hairline would be scrolled past. A discarded part's bar
 * goes grey rather than disappearing, because it is still a border — the one between what is
 * processed and what is not.
 *
 * Memoized for the same reason a segment is: the page re-renders ten times a second while it
 * plays, and a divider's props change only when the draft does.
 */
export const PartDivider = memo(function PartDivider({
  view,
  editable,
  autoFocus,
  onPatch,
  onMerge,
  onNudge,
  onSeek,
  startedAt,
}: {
  view: PartView;
  editable: boolean;
  autoFocus: boolean;
  onPatch: (position: number, change: Partial<DraftPart>) => void;
  onMerge: (position: number) => void;
  onNudge: (position: number, direction: "earlier" | "later") => void;
  onSeek: (seconds: number) => void;
  startedAt: string | null;
}) {
  const { position, count, startMs, endMs, verdict, title, description, reason, people, empty, earlier, later } = view;
  const meeting = verdict === "meeting";
  const name = title || "Untitled part";
  const titleField = useRef<HTMLInputElement>(null);
  const clockOf = (ms: number) => formatClockAt(startedAt, ms) ?? formatTimecode(ms);

  useEffect(() => {
    if (autoFocus) titleField.current?.focus();
  }, [autoFocus]);

  const pill =
    "inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:border-[var(--border-default)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[var(--border-subtle)] disabled:hover:text-[var(--text-secondary)]";

  return (
    <section
      data-part={position}
      aria-label={`Part ${position + 1} of ${count}: ${name}`}
      className={cn("scroll-mt-[72px] px-[var(--space-4)] pb-[var(--space-4)]", position === 0 ? "pt-[var(--space-2)]" : "pt-[var(--space-12)]")}
    >
      <div
        aria-hidden
        className="h-[6px] rounded-[var(--radius-full)] transition-colors"
        style={{
          background: meeting ? "var(--primary)" : "var(--border-strong)",
          boxShadow: meeting ? "0 0 24px -4px color-mix(in oklch, var(--primary) 60%, transparent)" : undefined,
        }}
      />

      <div className="mt-[var(--space-4)] flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-2)]">
        <span
          className={cn(
            "text-[length:var(--text-2xs)] font-bold uppercase tracking-[var(--tracking-wider)]",
            meeting ? "text-[var(--primary)]" : "text-[var(--text-muted)]",
          )}
        >
          Part {position + 1} of {count}
        </span>
        <button
          type="button"
          onClick={() => onSeek(startMs / 1000 + 0.01)}
          aria-label={`Play part ${position + 1} from ${clockOf(startMs)}`}
          className="font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)] transition-colors hover:text-[var(--vl-bridge-soft)]"
        >
          {clockOf(startMs)} – {clockOf(endMs)}
        </button>
        <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">{formatDuration(endMs - startMs)}</span>

        <div className="ml-auto">
          {editable ? (
            <VerdictToggle verdict={verdict} disabled={false} onChange={(next) => onPatch(position, { verdict: next })} />
          ) : (
            <span className="rounded-[var(--radius-full)] border border-[var(--border-default)] px-2 py-0.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
              {meeting ? "Meeting" : "Discarded"}
            </span>
          )}
        </div>
      </div>

      {editable ? (
        <>
          <input
            ref={titleField}
            type="text"
            value={title}
            onChange={(event) => onPatch(position, { titleOverride: event.target.value })}
            placeholder="Name this part"
            aria-label={`Title of part ${position + 1}`}
            className={cn(
              "-mx-1 mt-[var(--space-2)] w-[calc(100%+0.5rem)] rounded-[var(--radius-md)] border border-transparent bg-transparent px-1 text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] outline-none transition placeholder:text-[var(--text-muted)] hover:border-[var(--border-subtle)] focus:border-[var(--border-default)] focus:bg-[var(--surface-1)]",
              meeting ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
            )}
          />
          <textarea
            value={description}
            onChange={(event) => onPatch(position, { descriptionOverride: event.target.value })}
            placeholder="Description (optional)"
            aria-label={`Description of part ${position + 1}`}
            rows={2}
            className="-mx-1 mt-1 w-[calc(100%+0.5rem)] resize-none rounded-[var(--radius-md)] border border-transparent bg-transparent px-1 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)] outline-none transition placeholder:text-[var(--text-muted)] hover:border-[var(--border-subtle)] focus:border-[var(--border-default)] focus:bg-[var(--surface-1)]"
          />
        </>
      ) : (
        <>
          <h2 className="mt-[var(--space-2)] text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {name}
          </h2>
          {description && (
            <p className="mt-1 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">{description}</p>
          )}
        </>
      )}

      <div className="mt-[var(--space-3)] flex flex-wrap items-center gap-x-[var(--space-4)] gap-y-[var(--space-2)] text-[length:var(--text-xs)]">
        {people.length === 0 ? (
          <span className="text-[var(--text-muted)]">Nobody talks</span>
        ) : (
          <ul aria-label={`Who talks in part ${position + 1}`} className="flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-1">
            {people.map((person) => (
              <li key={person.name} className="flex items-center gap-1.5 whitespace-nowrap">
                <span aria-hidden className="h-[7px] w-[7px] rounded-full" style={{ background: person.colour }} />
                <span className="text-[var(--text-primary)]">{person.name}</span>
                <span className="tabular-nums text-[var(--text-muted)]">{Math.round(person.share * 100)}%</span>
              </li>
            ))}
          </ul>
        )}
        <span className="text-[var(--text-muted)]">{reason}</span>
      </div>

      {!meeting && (
        <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Discarded — its lines stay in the recording, and nothing in it is processed.
        </p>
      )}
      {empty && (
        <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          No line of the transcript starts in this part, so it will not become a meeting.
        </p>
      )}

      {editable && position > 0 && (
        <div className="mt-[var(--space-3)] flex flex-wrap items-center gap-[var(--space-2)]">
          <button type="button" onClick={() => onMerge(position)} className={pill}>
            <Merge size={12} strokeWidth={2} />
            Merge into part {position}
          </button>
          <button type="button" disabled={earlier === null} onClick={() => onNudge(position, "earlier")} className={pill}>
            <ChevronUp size={12} strokeWidth={2.5} />
            Start a line earlier
          </button>
          <button type="button" disabled={later === null} onClick={() => onNudge(position, "later")} className={pill}>
            <ChevronDown size={12} strokeWidth={2.5} />
            Start a line later
          </button>
        </div>
      )}
    </section>
  );
});

/**
 * The whole recording as its parts, under the header: one block per part as wide as it is long,
 * the one the playhead is in raised, and a line where the playhead is. It answers "where am I in
 * the day" while reading a column that only ever shows ten turns of it, and a click on a block
 * goes there.
 */
export function PartMap({
  views,
  durationMs,
  currentMs,
  editable,
  onJump,
  startedAt,
}: {
  views: PartView[];
  durationMs: number;
  currentMs: number;
  editable: boolean;
  onJump: (position: number) => void;
  startedAt: string | null;
}) {
  const clockOf = (ms: number) => formatClockAt(startedAt, ms) ?? formatTimecode(ms);
  const total = Math.max(1, durationMs, views.at(-1)?.endMs ?? 0);
  const current = partOf(views, currentMs) ?? views[0];
  const meetings = views.filter((view) => view.verdict === "meeting").length;
  const discarded = views.length - meetings;

  return (
    <nav
      aria-label="Parts"
      className="relative z-[3] flex shrink-0 flex-col gap-[var(--space-2)] border-b border-[var(--border-subtle)] bg-[var(--surface-glass-heavy)] px-[var(--space-6)] py-[var(--space-3)] backdrop-blur-[var(--blur-sm)]"
    >
      <div className="flex flex-wrap items-baseline gap-x-[var(--space-3)] gap-y-1">
        {current && (
          <p className="flex min-w-0 items-baseline gap-[var(--space-2)]">
            <span className="shrink-0 text-[length:var(--text-2xs)] font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--primary)]">
              Part {current.position + 1} of {views.length}
            </span>
            <span className="truncate text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
              {current.title || "Untitled part"}
            </span>
          </p>
        )}
        <p className="ml-auto shrink-0 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          {meetings} {meetings === 1 ? "meeting" : "meetings"}
          {discarded > 0 && ` · ${discarded} discarded`}
          {editable
            ? " · start them from the table when you are done"
            : " · a meeting has been started, so these parts can no longer change"}
        </p>
      </div>

      <div className="relative flex h-[14px] items-center">
        {views.map((view) => {
          const active = view.position === current?.position;
          return (
            <button
              key={view.startMs}
              type="button"
              onClick={() => onJump(view.position)}
              aria-current={active ? "true" : undefined}
              aria-label={`Part ${view.position + 1}: ${view.title || "Untitled part"}, ${clockOf(view.startMs)} to ${clockOf(view.endMs)}`}
              title={`${view.title || "Untitled part"} · ${clockOf(view.startMs)}–${clockOf(view.endMs)}`}
              className="group flex h-full min-w-[6px] items-center px-[1.5px]"
              style={{ width: `${((view.endMs - view.startMs) / total) * 100}%` }}
            >
              <span
                aria-hidden
                className={cn(
                  "block w-full rounded-[var(--radius-full)] transition-all group-hover:brightness-125",
                  active ? "h-[14px]" : "h-[8px]",
                )}
                style={{
                  background: view.verdict === "meeting" ? "var(--primary)" : "var(--border-strong)",
                  opacity: active ? 1 : 0.7,
                }}
              />
            </button>
          );
        })}

        <span
          aria-hidden
          className="pointer-events-none absolute top-[-3px] h-[20px] w-[2px] rounded-[1px] bg-[var(--vl-accent-warm)]"
          style={{
            left: `${Math.min(100, (currentMs / total) * 100)}%`,
            boxShadow: "0 0 8px 1px color-mix(in oklch, var(--vl-accent-warm) 55%, transparent)",
          }}
        />
      </div>
    </nav>
  );
}

// ------------------------------------------------------------------ one segment
